import { ExternalLink } from 'lucide-react';
import type { BackupCheck, BackupStatus } from '../../types/admin';

interface BackupCheckCardProps {
  check: BackupCheck;
}

const dotClass: Record<BackupStatus, string> = {
  up: 'bg-emerald-500',
  grace: 'bg-amber-500',
  down: 'bg-red-500',
  paused: 'bg-gray-400',
  new: 'bg-gray-400',
};

function relative(iso: string | null): string {
  if (!iso) return 'never';
  const diff = Math.max(0, Date.now() - Date.parse(iso));
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function relativeFuture(iso: string | null): string {
  if (!iso) return '—';
  const diff = Date.parse(iso) - Date.now();
  if (diff <= 0) return 'overdue';
  const s = Math.floor(diff / 1000);
  if (s < 60) return `in ${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `in ${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `in ${h}h`;
  const d = Math.floor(h / 24);
  return `in ${d}d`;
}

function humanizeSeconds(s: number | null): string {
  if (s === null) return '—';
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  if (s <= 86400) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86400)}d`;
}

export function BackupCheckCard({ check }: BackupCheckCardProps) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${dotClass[check.status]}`} aria-label={check.status} />
          <span className="font-medium text-gray-900 dark:text-gray-100 text-sm truncate">{check.name}</span>
        </div>
        <a
          href={check.detailUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Open in healthchecks.io: ${check.name}`}
          className="flex-shrink-0 ml-2"
        >
          <ExternalLink className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300" />
        </a>
      </div>
      <div className="text-xs text-gray-500 dark:text-gray-400">
        Last ping: {relative(check.lastPing)} · Next: {relativeFuture(check.nextPing)}
      </div>
      <div className="text-xs text-gray-500 dark:text-gray-400">
        Period: {humanizeSeconds(check.periodSeconds)} · Grace: {humanizeSeconds(check.graceSeconds)}
      </div>
    </div>
  );
}
